import { Link } from 'react-router-dom';
import { Button, Modal, ModalFooter, PolicyNumber, collapsedPolicyNumberSource } from '@/components/common';
import { getErrorMessage } from '@/services';
import type { CommissionPayoutWithPosp, PayoutLine } from '@/services/commissions';
import { usePayoutLines } from '@/hooks/useCommissions';
import { formatCurrency } from '@/utils';

/**
 * B13 — the policies inside one payout, POSP-wise: POSP Name, Policy (number +
 * holder), Product Name, Insurance Type, POSP Commission (Broker Commission on a
 * legacy broker-entity payout, whose POSP share is zero).
 *
 * Three states, never two: a failed request says it failed — it must not fall
 * into "this payout has no policies", which is a different (and alarming)
 * claim about money.
 */
export function PayoutLinesModal({
  payout,
  onClose,
}: {
  payout: CommissionPayoutWithPosp | null;
  onClose: () => void;
}) {
  const query = usePayoutLines(payout?.id);
  const lines = query.data?.results ?? [];
  // A legacy broker-entity payout carries the BROKER's share; its POSP share is
  // 0.00, so totalling posp_commission would show Rs 0.00 against a real gross.
  const commissionLabel =
    query.data?.commission_label ?? (payout?.entity_type === 'broker' ? 'Broker Commission' : 'POSP Commission');
  const amountOf = (line: PayoutLine) => line.commission_amount ?? line.posp_commission;
  const total = lines.reduce((sum, l) => sum + (Number(amountOf(l)) || 0), 0);
  const pospName = query.data?.posp_name ?? payout?.posp_name ?? payout?.posp_code ?? '';

  return (
    <Modal
      isOpen={Boolean(payout)}
      onClose={onClose}
      size="xl"
      title="Policies in this payout"
      description={payout ? `${payout.payout_reference}${pospName ? ` — ${pospName}` : ''}` : undefined}
    >
      {query.isLoading ? (
        <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">Loading policies…</p>
      ) : query.isError ? (
        <div className="py-6 text-center">
          <p className="text-sm font-semibold text-red-700 dark:text-red-400">Could not load the policies in this payout</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{getErrorMessage(query.error)}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => query.refetch()}>
            Try again
          </Button>
        </div>
      ) : lines.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">This payout carries no policies.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                {['POSP Name', 'Policy', 'Product Name', 'Insurance Type', commissionLabel].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {lines.map((line) => (
                <tr key={line.calculation}>
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{line.posp_name ?? '—'}</td>
                  <td className="px-4 py-2 text-sm">
                    <PolicyNumber
                      source={collapsedPolicyNumberSource(line.policy_number, line.insurer_policy_number)}
                      render={(text) =>
                        line.policy_id ? (
                          <Link
                            to={`/policies/${line.policy_id}`}
                            className="font-medium text-primary-600 hover:underline dark:text-primary-400"
                          >
                            {text}
                          </Link>
                        ) : (
                          <span className="font-medium text-gray-900 dark:text-gray-100">{text}</span>
                        )
                      }
                    />
                    {line.holder_name && (
                      <span className="block text-xs text-gray-500 dark:text-gray-400">{line.holder_name}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{line.product_name ?? '—'}</td>
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{line.insurance_type ?? '—'}</td>
                  <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                    {formatCurrency(amountOf(line))}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200 dark:border-gray-700">
                <td colSpan={4} className="px-4 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Total ({lines.length} {lines.length === 1 ? 'policy' : 'policies'})
                </td>
                <td className="px-4 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}
